import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CheckCircle2,
  Loader2,
  Package,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { DISCOUNT_TIERS } from '../../utils/discountTiers';

const DISCOUNT_LEVELS = DISCOUNT_TIERS.map((t) => ({
  value: t.level,
  label: `Tier ${t.level} - ${t.consumer}% off`,
  name: t.name,
}));

/**
 * BulkPostModal - Phase 1B Component
 * 
 * Modal for bulk posting products from Store Code Registry or Frequently Scanned.
 * Allows setting default quantity and discount tier for all selected items.
 */
export default function BulkPostModal({ 
  open, 
  onOpenChange, 
  selectedProducts,
  source = 'store_registry',
  onSuccess 
}) {
  const { t } = useLanguage();
  
  const [defaultQuantity, setDefaultQuantity] = useState(10);
  const [defaultDiscount, setDefaultDiscount] = useState(2);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  
  // Product-specific overrides
  const [overrides, setOverrides] = useState({});

  const updateOverride = useCallback((barcode, field, value) => {
    setOverrides(prev => ({
      ...prev,
      [barcode]: {
        ...(prev[barcode] || {}),
        [field]: value
      }
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedProducts || selectedProducts.length === 0) return;
    
    setSubmitting(true);
    setResults(null);
    
    try {
      // Prepare items for API
      const items = selectedProducts.map(product => {
        const barcode = product.barcode || product.raw_code;
        const override = overrides[barcode] || {};
        
        return {
          barcode,
          quantity: override.quantity || defaultQuantity,
          discount_level: override.discount_level || product.discount_level || defaultDiscount,
          regular_price: override.regular_price || product.regular_price || null
        };
      });
      
      const response = await api.post('/barcode/batch-repost', {
        items,
        source
      });
      
      if (response.data.success) {
        setResults(response.data);
        
        const { created, failed } = response.data.summary;
        
        if (created > 0) {
          toast.success(`Successfully posted ${created} products!`);
          if (onSuccess) {
            onSuccess(response.data);
          }
        }
        
        if (failed > 0) {
          toast.warning(`${failed} products could not be posted`);
        }
        
        // Auto-close if all succeeded
        if (failed === 0) {
          setTimeout(() => {
            onOpenChange(false);
            setResults(null);
            setOverrides({});
          }, 1500);
        }
      }
    } catch (err) {
      console.error('Batch repost error:', err);
      toast.error(err.response?.data?.detail || 'Failed to post products');
    } finally {
      setSubmitting(false);
    }
  }, [selectedProducts, defaultQuantity, defaultDiscount, overrides, source, onSuccess, onOpenChange]);

  const handleClose = useCallback(() => {
    if (!submitting) {
      onOpenChange(false);
      setResults(null);
      setOverrides({});
    }
  }, [submitting, onOpenChange]);

  if (!selectedProducts || selectedProducts.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            {t('bulk_post.title', 'Bulk Post Products')}
          </DialogTitle>
          <DialogDescription>
            {t('bulk_post.description', `Post ${selectedProducts.length} products with default settings`)}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto space-y-4 py-4">
          {/* Default Settings */}
          {!results && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {t('bulk_post.default_settings', 'Default Settings (apply to all)')}
              </h4>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label className="text-xs text-gray-500">Quantity</Label>
                  <Input
                    type="number"
                    min={5}
                    value={defaultQuantity}
                    onChange={(e) => setDefaultQuantity(parseInt(e.target.value, 10) || 10)}
                    className="mt-1"
                    data-testid="default-quantity"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-gray-500">Discount Tier</Label>
                  <Select 
                    value={defaultDiscount.toString()} 
                    onValueChange={(v) => setDefaultDiscount(parseInt(v, 10))}
                  >
                    <SelectTrigger className="mt-1" data-testid="default-discount">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DISCOUNT_LEVELS.map(level => (
                        <SelectItem key={level.value} value={level.value.toString()}>
                          {level.name} ({level.label.split(' - ')[1]})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          
          {/* Results Summary */}
          {results && (
            <div className={`p-4 rounded-lg ${results.summary.failed > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className={`w-5 h-5 ${results.summary.failed > 0 ? 'text-yellow-600' : 'text-green-600'}`} />
                <span className="font-medium">
                  {results.summary.created} of {results.summary.total} products posted
                </span>
              </div>
              {results.failed && results.failed.length > 0 && (
                <div className="mt-2 text-sm text-yellow-700">
                  <p className="font-medium">Failed items:</p>
                  <ul className="list-disc list-inside">
                    {results.failed.map((item, i) => (
                      <li key={i}>{item.barcode}: {item.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {/* Products List */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Product</TableHead>
                  <TableHead className="w-24">Price</TableHead>
                  <TableHead className="w-20">Qty</TableHead>
                  <TableHead className="w-28">Tier</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedProducts.map((product) => {
                  const barcode = product.barcode || product.raw_code;
                  const override = overrides[barcode] || {};
                  const createdItem = results?.created?.find(i => i.barcode === barcode);
                  const failedItem = results?.failed?.find(i => i.barcode === barcode);
                  
                  return (
                    <TableRow 
                      key={barcode} 
                      className={createdItem ? 'bg-green-50' : failedItem ? 'bg-red-50' : ''}
                      data-testid={`bulk-item-${barcode}`}
                    >
                      <TableCell>
                        <div>
                          <span className="font-medium text-sm">
                            {product.product_name || product.name || 'Unknown Product'}
                          </span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <code className="text-[10px] font-mono bg-gray-100 px-1 py-0.5 rounded">
                              {barcode}
                            </code>
                            {product.category && (
                              <Badge variant="outline" className="text-[9px]">
                                {product.category}
                              </Badge>
                            )}
                            {createdItem && (
                              <Badge className="text-[9px] bg-green-100 text-green-700">
                                <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                                Posted
                              </Badge>
                            )}
                            {failedItem && (
                              <Badge className="text-[9px] bg-red-100 text-red-700">
                                <AlertCircle className="w-2.5 h-2.5 mr-0.5" />
                                Failed
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {product.regular_price ? (
                          <span className="text-sm">${product.regular_price.toFixed(2)}</span>
                        ) : (
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            placeholder="$0.00"
                            value={override.regular_price || ''}
                            onChange={(e) => updateOverride(barcode, 'regular_price', parseFloat(e.target.value))}
                            className="h-7 text-xs"
                            disabled={!!results}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min={5}
                          value={override.quantity || defaultQuantity}
                          onChange={(e) => updateOverride(barcode, 'quantity', parseInt(e.target.value, 10))}
                          className="h-7 text-xs w-16"
                          disabled={!!results}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={(override.discount_level || product.discount_level || defaultDiscount).toString()}
                          onValueChange={(v) => updateOverride(barcode, 'discount_level', parseInt(v, 10))}
                          disabled={!!results}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DISCOUNT_LEVELS.map(level => (
                              <SelectItem key={level.value} value={level.value.toString()} className="text-xs">
                                {level.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            {results ? t('common.close', 'Close') : t('common.cancel', 'Cancel')}
          </Button>
          {!results && (
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="min-w-[140px]"
              data-testid="bulk-post-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Post {selectedProducts.length} Items
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
